import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { ClientLogo } from '../../types.ts';
import {
  Star,
  Quote,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Building2,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';

// High-quality default client logos for corporate & BUMN clients
const defaultClientLogos: ClientLogo[] = [
  {
    id: 'logo-1',
    name: 'PT Nusantara Energi',
    category: 'BUMN & Energi',
    logoUrl: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-2',
    name: 'Bank Mandiri Corporate',
    category: 'Perbankan & Keuangan',
    logoUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-3',
    name: 'Telkom Indonesia Group',
    category: 'Telekomunikasi',
    logoUrl: 'https://images.unsplash.com/photo-1516876437184-593fda40c7ce?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-4',
    name: 'Pertamina Patra Niaga',
    category: 'BUMN Migas',
    logoUrl: 'https://images.unsplash.com/photo-1542744094-3a31b272c490?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-5',
    name: 'Kuliner Rempah Nusantara',
    category: 'F&B & Ekspor UMKM',
    logoUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-6',
    name: 'Dinas Pariwisata & Kebudayaan',
    category: 'Instansi Pemerintah',
    logoUrl: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-7',
    name: 'PT PLN (Persero)',
    category: 'Kelistrikan Nasional',
    logoUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=300&q=80',
  },
  {
    id: 'logo-8',
    name: 'Astra International',
    category: 'Otomotif & Industri',
    logoUrl: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?auto=format&fit=crop&w=300&q=80',
  },
];

export default function TestimonialsSection({
  badge = 'Trusted by 500+ Organizations',
  title = 'Client Testimonials & Partner Endorsements',
  subtitle = 'Feedback and verified credentials from leading enterprises, climate institutions, and clean tech partners',
}: {
  badge?: string;
  title?: string;
  subtitle?: string;
}) {
  const { settings } = useApp();
  const primaryColor = settings?.theme.primaryColor || '#059669';

  const testimonialsConfig = settings?.testimonialsConfig;
  const configDisplayMode = testimonialsConfig?.displayMode || 'both'; // 'reviews' | 'logos' | 'both'
  const clientLogos = testimonialsConfig?.clientLogos?.length ? testimonialsConfig.clientLogos : defaultClientLogos;

  // Active View Tab State if mode is 'both'
  const [activeTab, setActiveTab] = useState<'reviews' | 'logos'>(
    configDisplayMode === 'logos' ? 'logos' : 'reviews'
  );

  useEffect(() => {
    if (configDisplayMode === 'logos') {
      setActiveTab('logos');
    } else if (configDisplayMode === 'reviews') {
      setActiveTab('reviews');
    }
  }, [configDisplayMode]);

  // Carousel slider state for logos
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleCount, setVisibleCount] = useState(4);
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 640) setVisibleCount(2);
      else if (window.innerWidth < 768) setVisibleCount(3);
      else if (window.innerWidth < 1024) setVisibleCount(4);
      else setVisibleCount(5);
    };
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const reviews = testimonialsConfig?.reviewsList?.length
    ? testimonialsConfig.reviewsList
    : [
        {
          id: '1',
          name: 'Dr. Arthur Pendelton',
          role: 'Chief Sustainability Officer - Global Clean Energy Corp',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&q=80',
          comment:
            'Pendampingan hukum dan analisis perkara yang diberikan oleh tim Advokat ALY LAW FIRM sangat profesional, tegas, dan memberikan kepastian hukum yang jelas bagi korporasi kami.',
          rating: 5,
          date: 'January 2026',
        },
        {
          id: '2',
          name: 'Elena Rostova',
          role: 'Director of Environmental Impact - EcoTech Ventures',
          avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=120&q=80',
          comment:
            'Their life-cycle assessment models and talent development programs empowered our technical teams to achieve carbon reduction targets 6 months ahead of schedule.',
          rating: 5,
          date: 'December 2025',
        },
        {
          id: '3',
          name: 'Marcus Vance, M.Eng.',
          role: 'Head of Operations - Renewable Grid Systems',
          avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&q=80',
          comment:
            'World-class academic rigor combined with practical industrial sustainability intelligence. Highly recommended for any forward-thinking institution.',
          rating: 5,
          date: 'November 2025',
        },
      ];

  // Auto-play logic for carousel
  useEffect(() => {
    if (isAutoPlaying && clientLogos.length > 0) {
      autoPlayTimerRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % clientLogos.length);
      }, 3000);
    }
    return () => {
      if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
    };
  }, [isAutoPlaying, clientLogos.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? clientLogos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % clientLogos.length);
  };

  return (
    <section id="testimonials-section" className="py-12 bg-slate-50/60 border-y border-slate-200/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold mb-3 shadow-2xs">
            <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
            <span>{testimonialsConfig?.badge || badge}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {testimonialsConfig?.title || title}
          </h2>
          {(testimonialsConfig?.subtitle || subtitle) && (
            <p className="text-sm sm:text-base text-slate-500 mt-2 font-medium">
              {testimonialsConfig?.subtitle || subtitle}
            </p>
          )}
        </div>

        {/* LOGO CAROUSEL / SLIDE VIEW (If displayMode is 'logos' or 'both') */}
        {(configDisplayMode === 'logos' || configDisplayMode === 'both') && (
          <div
            className={`relative bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs overflow-hidden ${
              configDisplayMode === 'both' ? 'mb-10' : ''
            }`}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Slide {currentIndex + 1} of {clientLogos.length}
                </span>
              </div>
              
              {/* Manual Nav Controls */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handlePrev}
                  aria-label="Previous logo slide"
                  className="p-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  aria-label="Next logo slide"
                  className="p-1.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Single Row Automatic Horizontal Carousel Slider Track */}
            <div className="overflow-hidden w-full py-2">
              <div
                className="flex transition-transform duration-500 ease-in-out -mx-2"
                style={{
                  transform: `translateX(-${(currentIndex % clientLogos.length) * (100 / visibleCount)}%)`,
                }}
              >
                {clientLogos.concat(clientLogos).map((item, idx) => (
                  <div
                    key={`${item.id || idx}-${idx}`}
                    className="w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 shrink-0 px-2"
                  >
                    <div className="bg-slate-50/90 hover:bg-white rounded-2xl p-4 border border-slate-200/80 hover:border-emerald-500 transition-all flex flex-col items-center justify-between text-center h-full group cursor-pointer shadow-2xs hover:shadow-sm">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-white border border-slate-200/80 flex items-center justify-center p-2 mb-2.5 shadow-2xs group-hover:scale-105 transition-transform overflow-hidden shrink-0">
                        <img
                          src={item.logoUrl}
                          alt={item.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="w-full flex-1 flex flex-col items-center justify-center">
                        <div className="font-extrabold text-xs sm:text-sm text-slate-900 leading-snug group-hover:text-emerald-700 transition-colors w-full text-center break-words px-1">
                          {item.name}
                        </div>
                        {item.category && (
                          <span className="text-[10px] font-semibold text-slate-500 mt-1 px-2.5 py-0.5 bg-slate-200/60 rounded-full text-center break-words inline-block max-w-full">
                            {item.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Indicators */}
            <div className="flex items-center justify-center gap-1.5 mt-6 pt-4 border-t border-slate-100">
              {clientLogos.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    idx === currentIndex
                      ? 'w-6 bg-emerald-600'
                      : 'w-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {/* REVIEWS GRID VIEW (If displayMode is 'reviews' or 'both') */}
        {(configDisplayMode === 'reviews' || configDisplayMode === 'both') && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((rev, idx) => (
              <div
                key={rev.id || idx}
                className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-xs hover:shadow-md transition-all flex flex-col justify-between relative"
              >
                <Quote className="w-8 h-8 text-slate-200 absolute top-5 right-5" />

                <div>
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: rev.rating || 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed italic">
                    &ldquo;{rev.comment}&rdquo;
                  </p>
                </div>

                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
                  <img
                    src={rev.avatar}
                    alt={rev.name}
                    className="w-10 h-10 rounded-full object-cover border border-slate-200 shrink-0"
                  />
                  <div>
                    <div className="font-bold text-xs text-slate-900 flex items-center gap-1">
                      <span>{rev.name}</span>
                      <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                    </div>
                    <div className="text-[11px] text-slate-500">{rev.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

